import * as d3 from 'd3';

import { IWidget } from '../../widgets';
import { Page, PrintableDashboard, PrintWidget, Position, IGridsterOptions, PageBreak } from '../../models';
import { IPrintConfiguration, orientation as Orientation, format as Format, pageOptions } from '../../services/helpers';
import { rowsCount, computeSizeInPixels } from '../../services/helpers/print/methods';
import { changeColumnNumber } from './positionComputer';

export interface IPrintHelper {
    changeColumnNumber: (widgets: IWidget[], nCol: number) => PrintWidget[];
    createPrintableDashboard(widgets: PrintWidget[], nRow: number, nCol: number, widgetMargin: number, pageWidth: number, pageHeight: number): PrintableDashboard;
    getPageBreaks(widgetList: IWidget[], gridsterOptions: IGridsterOptions, printConfiguration: IPrintConfiguration): PageBreak[];
    getPageBreaks(widgetList: IWidget[], gridsterOptions: IGridsterOptions, printConfiguration: IPrintConfiguration, adapter: number): PageBreak[];
    rowsCount(printConfiguration: IPrintConfiguration, widgetMargin: number, rowHeight: number, adapter: number): number;
    computeSizeInPixels(widgetMargin: number, printConfiguration: IPrintConfiguration, adapter: number): { width: number, height: number };
}

export class PrintHelper implements IPrintHelper {
    static helperId = 'printHelper';

    changeColumnNumber(widgets: PrintWidget[], nCol: number): PrintWidget[] {
        return changeColumnNumber(widgets, nCol);
    }

    createPrintableDashboard(widgets: PrintWidget[], nRow: number, nCol: number, widgetMargin: number, pageWidth: number, pageHeight: number): PrintableDashboard {
        const result = new PrintableDashboard();
        result.pages.push(new Page());
        let page = result.pages[0];
        let offset = 0;
        widgets.forEach(w => {
            let row = w.printPosition.row - offset;
            const sizeY = w.printPosition.sizeY;

            if ((row) >= nRow || (row + sizeY) > nRow) {
                page = new Page();
                result.pages.push(page);
                offset += row;
                row = 0;
            }

            w.printPosition.row = row;

            //TODO check is not correct
            //if (widgets.filter(wi => wi.printPosition.row === w.printPosition.row).length === 1) {
            //    w.printPosition.sizeX = nCol;
            //}

            const style = this.computeSize(pageWidth / nCol, pageHeight / nRow, widgetMargin, w.printPosition);
            w.style = {
                'height': style.height + 'px',
                'width': style.width + 'px',
                'position': 'absolute',
                'left': style.left + 'px',
                'top': style.top + 'px'
            };
            const headerHeight = w.isTitleHidden ? 0 : 32;
            w.containerStyle = {
                'height': (style.height - headerHeight) + 'px' //32px is the height of the header
            };
            page.widgets.push(w);
        });
        return result;
    }

    getPageBreaks(widgetList: IWidget[], gridsterOptions: IGridsterOptions, printConfiguration: IPrintConfiguration, adapter: number = 0): PageBreak[] {
        const pageBreaks: PageBreak[] = [];
        try {
            const { rowHeight, margins } = gridsterOptions;
            const maxBottom = d3.max(widgetList, w => w.row + w.sizeY);
            let { printOptions } = printConfiguration;
            if (!printOptions) {
                printOptions = { format: Format.A3, orientation: Orientation.Portrait, columns: 0, rows: 0 };
            }
            const { format, orientation } = printOptions;
            const configuration = pageOptions.map(p => p.configuration).find(c => c.format === format && c.orientation === orientation);
            if (configuration) {
                //Protection for A4 portrait at the moment   
                if (configuration.columns === gridsterOptions.columns) {
                    const rh = +rowHeight * .7;
                    const rows = rowsCount(printConfiguration, margins[0] * .7, rh, adapter);
                    const pageHeight = rows * (+rowHeight);
                    let i = 1;
                    while (i * rows < maxBottom) {
                        pageBreaks.push({
                            top: i * pageHeight + 'px'
                        });
                        i++;
                    }
                }
            }
        } catch (e) {
            console.error(e);
        }
        return pageBreaks;
    }

    rowsCount(printConfiguration: IPrintConfiguration, widgetMargin: number, rowHeight: number, adapter: number): number {
        return rowsCount(printConfiguration, widgetMargin, rowHeight, adapter);
    }

    computeSizeInPixels(widgetMargin: number, printConfiguration: IPrintConfiguration, adapter: number) {
        return computeSizeInPixels(widgetMargin, printConfiguration, adapter);
    }

    private computeSize(colWidth: number, rowHeight: number, widgetMargin: number, position: Position) {
        const width = (position.sizeX * colWidth - widgetMargin);
        const height = (position.sizeY * rowHeight - widgetMargin);
        const left = position.col * colWidth + widgetMargin;
        const top = position.row * rowHeight + widgetMargin;
        return {
            width: width,
            height: height,
            left: left,
            top: top
        };
    }
}
